import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { CallbackScreen, LogoutScreen } from './auth/CallbackScreen';
import { LoginScreen } from './auth/LoginScreen';
import { ResetPasswordScreen } from './auth/ResetPasswordScreen';
import { SignUpScreen } from './auth/SignUpScreen';
import { AdminScreen } from './admin/AdminScreen';
import { DesktopScreen } from './screens/DesktopScreen';
import { OnboardingScreen } from './screens/OnboardingScreen';
import { MobileInterstitial } from './components/MobileInterstitial';
import { JotlyMark } from './components/JotlyMark';
import { useIsNarrow } from './hooks/useViewport';

const ONBOARDED_KEY = 'jotly.onboarded';

export function App() {
  const { authed, setAuthed, signOut, blockedMessage, isAdmin } = useAuth();
  const narrow = useIsNarrow();
  const location = useLocation();
  const [onboarded, setOnboarded] = useState(() => localStorage.getItem(ONBOARDED_KEY) === '1');

  // Safety net: if AuthProvider's effect never resolved `authed` for any
  // reason (browser quirk, dropped microtask, dev tooling), force it to false
  // after 5s so the login screen still renders instead of a permanent splash.
  useEffect(() => {
    if (authed !== null) return;
    const t = setTimeout(() => setAuthed(false), 5000);
    return () => clearTimeout(t);
  }, [authed, setAuthed]);

  // Gate 1: Onboarding — first run only, no auth required.
  // The onboarded flag is synchronous (localStorage), so we can render
  // immediately without waiting for Cognito to resolve.
  if (!onboarded) {
    return <OnboardingScreen onComplete={() => setOnboarded(true)} />;
  }

  // Gate 2: Auth — only checked after onboarding is done.
  if (authed === null) {
    return <LoadingCanvas />;
  }

  if (blockedMessage) {
    return <BlockedCanvas message={blockedMessage} onSignOut={signOut} />;
  }

  return (
    <Routes>
      <Route path="/login" element={authed ? <Navigate to="/" replace /> : <LoginScreen />} />
      <Route path="/signup" element={authed ? <Navigate to="/" replace /> : <SignUpScreen />} />
      {/* Hosted password reset page — reachable without auth. The Cognito
          CustomMessage trigger emails this URL with code + email query params. */}
      <Route path="/reset-password" element={<ResetPasswordScreen />} />
      <Route path="/auth/callback" element={<CallbackScreen />} />
      <Route path="/auth/logout" element={<LogoutScreen />} />
      <Route path="/admin/*" element={authed && isAdmin ? <AdminScreen /> : <Navigate to="/" replace />} />
      <Route
        path="/*"
        element={
          authed
            ? narrow
              ? <MobileInterstitial />
              : <DesktopScreen />
            : <Navigate to="/login" replace state={{ from: location }} />
        }
      />
    </Routes>
  );
}

function BlockedCanvas({ message, onSignOut }: { message: string; onSignOut: () => void }) {
  return (
    <div style={loadingCanvasStyle}>
      <div style={blockedCardStyle}>
        <JotlyMark size={28} />
        <h1 style={blockedTitleStyle}>Account suspended</h1>
        <p style={blockedHintStyle}>{message}</p>
        <button type="button" onClick={onSignOut} style={blockedButtonStyle}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function LoadingCanvas() {
  // Mirror the login screen's identity treatment: mark + wordmark stacked,
  // muted. Used during the brief auth-resolve window before the desktop
  // shell takes over.
  return (
    <div style={loadingCanvasStyle}>
      <div style={loadingStackStyle}>
        <JotlyMark size={28} />
        <div className="rc-kicker">Jotly</div>
      </div>
    </div>
  );
}

const loadingCanvasStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--rc-bg)',
};

const loadingStackStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  opacity: 0.85,
};

const blockedCardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background: 'var(--rc-card)',
  borderRadius: 18,
  padding: '34px 32px 28px',
  boxShadow: '0 12px 36px rgba(20,22,27,0.06), 0 1px 0 rgba(20,22,27,0.03)',
  border: '1px solid var(--rc-border)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 14,
};

const blockedTitleStyle: React.CSSProperties = {
  font: '700 24px/1.1 var(--rc-display)',
  margin: 0,
  letterSpacing: '-0.02em',
  textAlign: 'center',
};

const blockedHintStyle: React.CSSProperties = {
  margin: 0,
  font: '400 14px/1.5 var(--rc-sans)',
  color: 'var(--rc-ink-2)',
  textAlign: 'center',
};

const blockedButtonStyle: React.CSSProperties = {
  marginTop: 8,
  appearance: 'none',
  border: 0,
  cursor: 'pointer',
  padding: '10px 18px',
  borderRadius: 12,
  background: 'var(--rc-ink)',
  color: '#fff',
  font: '600 14px/1 var(--rc-sans)',
};
